import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../ui/Icon';
import PastelButton from '../ui/PastelButton';
import { useIsMobile } from '../../utils/mobileDetection';

const SendTreatsModal = ({ isOpen, onClose, onSendTreat, petName, angelPet }) => {
  const [selectedTreat, setSelectedTreat] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useIsMobile();

  // New local state for inline confirmation flow
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [angelTreatReply, setAngelTreatReply] = useState(null);
  const [sendError, setSendError] = useState(null);

  const treatOptions = [
    { id: "chicken_jerky", label: "Chicken Jerky Strip", subtitle: "A heavenly slice of home", icon: "🍗" },
    { id: "tuna_puff", label: "Tuna Puff", subtitle: "A cloud-soft seafood blessing", icon: "🐟" },
    { id: "lamb_bite", label: "Lamb Crunch Bite", subtitle: "A savory star from above", icon: "🦴" },
    { id: "pumpkin_biscuit", label: "Pumpkin Biscuit", subtitle: "A golden treat kissed by autumn", icon: "🎃" },
    { id: "yogurt_drop", label: "Yogurt Drop", subtitle: "A creamy kiss from the skies", icon: "🍼" },
  ];

  // Configure motion props based on device type
  const getButtonMotionProps = (scaleAmount = 1.02) => {
    if (isMobile) {
      return {
        whileTap: { scale: 0.95 },
        transition: { duration: 0.1, ease: "easeOut" }
      };
    } else {
      return {
        whileHover: { scale: scaleAmount },
        whileTap: { scale: 0.95 },
        transition: { duration: 0.2, ease: "easeOut" }
      };
    }
  };

  const handleTreatSelect = (treat) => {
    setSelectedTreat(treat);
  };

  // Treat-specific reaction messages (local fallback if API doesn't return one)
  const treatReactions = {
    chicken_jerky: [
      "That jerky was divine! 😋\nJust like the ones from home!",
      "Mmm, so savory and perfect! ✨\nYou know exactly what I love!",
      "Crunchy heaven in my mouth! 🌟\nThank you, sweet hooman!"
    ],
    tuna_puff: [
      "Fresh ocean breeze in every bite! 🌊\nThat tuna puff was magical!",
      "Fluffy clouds of fishy goodness! ☁️\nMy whiskers are tingling with joy!",
      "Swimming in flavor paradise! 🐟\nYou spoil me so wonderfully!"
    ],
    lamb_bite: [
      "Tender lamb from the celestial fields! 🌿\nAbsolutely scrumptious!",
      "Heavenly herbs and perfect texture! 🌟\nMy taste buds are dancing!",
      "Angelic lamb that melts my heart! 💕\nYou're the best treat-giver ever!"
    ],
    pumpkin_biscuit: [
      "Autumn magic in every crumb! 🍂\nThese pumpkin biscuits are heavenly!",
      "Golden sunset flavors! 🌅\nSpiced to perfection, dear hooman!",
      "Harvest moon sweetness! 🎃\nI'm purring with pumpkin bliss!"
    ],
    yogurt_drop: [
      "Creamy cloud kisses! ☁️\nThese yogurt drops are pure angel food!",
      "Smooth silk on my tongue! ✨\nLike drinking liquid starlight!",
      "Heavenly cream dreams! 💭\nYou know the way to my heart!"
    ]
  };

  const getRandomReaction = (treatId) => {
    const messages = treatReactions[treatId] || ["That treat was amazing! Thank you! 💕"];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const handleSendTreat = async () => {
    if (!selectedTreat || isSending || isSent) return;
    
    setIsSending(true);
    setSendError(null);
    setIsAnimating(true);
    
    try {
      // Call the parent's sendTreat function and wait for result
      const result = await onSendTreat(selectedTreat);
      
      // Success! Show inline response
      setIsSending(false);
      setIsSent(true);
      
      // Use the returned message or generate a local one
      const replyMessage = result?.reactionMessage || getRandomReaction(selectedTreat.id);
      setAngelTreatReply(replyMessage);
      
    } catch (error) {
      console.error('Error sending treat:', error);
      setIsSending(false);
      setSendError('Failed to send treat. Please try again.');
    } finally {
      setIsAnimating(false);
    }
  };

  const handleSendAnother = () => {
    // Reset all state to allow sending another treat
    setSelectedTreat(null);
    setIsSent(false);
    setAngelTreatReply(null);
    setSendError(null);
  };

  const handleClose = () => {
    if (isAnimating || isSending) return;
    // Reset all state when closing
    setSelectedTreat(null);
    setIsSent(false);
    setAngelTreatReply(null);
    setSendError(null);
    onClose();
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="heaven-background">
          <div className="container min-h-[calc(100vh-96px)] sm:p-4">
            <div className="relative text-center mb-6 pt-4">
              <button
                onClick={handleClose}
                disabled={isAnimating}
                className="absolute -top-4 -left-4 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
              >
                ← Back
              </button>
              
              <div className="secondary-page-header">
                <h2 className="secondary-page-title">Send Treats</h2>
                <p className="secondary-page-subtitle">Choose a heavenly treat for Angel {petName || 'Pet'}</p>
              </div>
              
              <div className="secondary-page-divider"></div>
            </div>
          
          <motion.div
            className="secondary-page-modal sm:bg-white/95 sm:backdrop-blur-[20px] sm:rounded-3xl sm:p-6 sm:shadow-xl sm:border sm:border-white/20 sm:max-w-md"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Angel Pet Avatar with Speech Bubble */}
            <div className="text-center mb-6">
              <div className="flex flex-col items-center">
                {/* Speech Bubble - appears ABOVE the avatar when sent */}
                <AnimatePresence>
                  {isSent && angelTreatReply && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: 20 }}
                      transition={{ type: "spring", damping: 20, stiffness: 300 }}
                      className="relative mb-3 max-w-[280px]"
                    >
                      {/* Success confirmation badge */}
                      <motion.div
                        initial={{ scale: 0, y: -10 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ type: "spring", damping: 15, delay: 0.1 }}
                        className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"
                      >
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                          ✓ Treat sent! 💝
                        </span>
                      </motion.div>

                      {/* Speech bubble with gentle float animation */}
                      <motion.div
                        className="relative pt-2"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <div className="bg-gradient-to-br from-white/95 to-pink-50/95 backdrop-blur-sm rounded-3xl px-5 py-4 shadow-xl border-2 border-pink-100/50 relative">
                          <p className="text-xs font-semibold text-purple-600 mb-1 text-center">
                            Angel {petName || 'Pet'} says:
                          </p>
                          <p className="text-gray-700 font-medium leading-relaxed whitespace-pre-line text-sm text-center">
                            {angelTreatReply}
                          </p>
                          
                          {/* Speech bubble tail pointing DOWN to the avatar */}
                          <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[14px] border-l-transparent border-r-transparent border-t-white/95"></div>
                          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-r-[16px] border-t-[16px] border-l-transparent border-r-transparent border-t-pink-100/50"></div>
                        </div>

                        {/* Floating hearts around the bubble */}
                        <div className="absolute inset-0 pointer-events-none">
                          {[...Array(4)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute text-pink-300"
                              style={{
                                left: `${-5 + i * 30}%`,
                                top: `${15 + i * 18}%`,
                                fontSize: '14px'
                              }}
                              animate={{
                                y: [0, -12, 0],
                                opacity: [0.6, 1, 0.6],
                                scale: [0.8, 1.1, 0.8],
                                rotate: [0, 5, -5, 0]
                              }}
                              transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                delay: i * 0.4,
                                ease: "easeInOut"
                              }}
                            >
                              💕
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Angel Avatar - larger when sent, with bounce animation */}
                <motion.div 
                  className="relative inline-block"
                  animate={isSent ? { 
                    scale: [1, 1.1, 1.05, 1],
                    rotate: [0, -3, 3, 0]
                  } : {}}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <div className={`rounded-full overflow-hidden border-3 shadow-xl transition-all duration-300 ${
                    isSent 
                      ? 'w-24 h-24 border-pink-300/70' 
                      : 'w-16 h-16 border-white/60'
                  }`}>
                  <img 
                    src={angelPet || '/angel_images/default_angel.png'}
                    alt={`Angel ${petName || 'Pet'}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/angel_images/default_angel.png';
                    }}
                  />
                </div>
                  {/* Glow effect - more prominent when sent */}
                  <div className={`absolute -inset-1 rounded-full animate-pulse transition-all duration-300 ${
                    isSent 
                      ? 'bg-gradient-to-r from-pink-300/50 via-purple-300/50 to-pink-300/50' 
                      : 'bg-gradient-to-r from-purple-200/40 to-pink-200/40'
                  }`}></div>
                  
                  {/* Sparkles around avatar when sent */}
                  {isSent && (
                    <div className="absolute inset-0 pointer-events-none">
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute text-yellow-400"
                          style={{
                            left: `${10 + (i * 60) % 100}%`,
                            top: `${5 + (i * 30) % 90}%`,
                            fontSize: '10px'
                          }}
                          animate={{
                            scale: [0.8, 1.2, 0.8],
                            opacity: [0.5, 1, 0.5],
                            rotate: [0, 180, 360]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: i * 0.3,
                            ease: "easeInOut"
                          }}
                        >
                          ✨
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>

                {/* Pet name label - shown when sent */}
                {isSent && (
                  <motion.p
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm font-semibold text-purple-600 mt-2"
                  >
                    Angel {petName || 'Pet'} ✨
                  </motion.p>
                )}
              </div>
            </div>

            {/* Treat Grid - Hidden when treat is sent */}
            {!isSent && (
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-3 mb-6">
                {treatOptions.map((treat) => (
                  <motion.button
                    key={treat.id}
                    onClick={() => handleTreatSelect(treat)}
                      disabled={isSending || isSent}
                    className={`relative aspect-square rounded-2xl border-2 transition-all duration-200 text-center disabled:opacity-50 overflow-hidden ${
                      selectedTreat?.id === treat.id
                        ? 'border-purple-400 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg scale-105'
                        : 'border-gray-200 bg-white/80 hover:border-purple-200 hover:bg-purple-50/50 hover:shadow-md'
                    }`}
                    {...getButtonMotionProps(1.05)}
                  >
                    {/* Heart indicator in top right */}
                    <div className="absolute top-1 right-1 z-10">
                      {selectedTreat?.id === treat.id ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-4 h-4 text-pink-500 text-sm"
                        >
                          💕
                        </motion.div>
                      ) : (
                        <div className="w-4 h-4 text-gray-300 text-sm">💕</div>
                      )}
                    </div>

                    {/* Treat icon */}
                    <div className="flex items-center justify-center h-full">
                      <span className="text-2xl">{treat.icon}</span>
                    </div>

                    {/* Treat label */}
                    <div className="absolute bottom-1 left-1 right-1">
                      <p className="text-xs font-medium text-gray-700 truncate px-1">
                        {treat.label}
                      </p>
                    </div>

                    {/* Selection overlay */}
                    {selectedTreat?.id === treat.id && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0 bg-purple-500/10 rounded-2xl"
                      />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
            )}

            {/* Selected treat subtitle - Only show when not sent */}
            {selectedTreat && !isSent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-4"
              >
                <p className="text-xs text-gray-600 italic bg-gray-50 rounded-lg py-2 px-3">
                  "{selectedTreat.subtitle}"
                </p>
              </motion.div>
            )}

            {/* Sending state message */}
            {isSending && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-4"
              >
                <div className="bg-gradient-to-br from-blue-50/95 to-purple-50/95 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-lg border-2 border-blue-100/50">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full"
                    />
                    <p className="text-sm font-medium">Angel {petName} is receiving your treat…</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Error message */}
            {sendError && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-4"
              >
                <div className="bg-red-50 rounded-2xl px-5 py-4 shadow-md border border-red-200">
                  <p className="text-sm text-red-600 font-medium">{sendError}</p>
                </div>
              </motion.div>
            )}

            {/* Action Buttons - Dynamic based on state */}
            <div className="flex gap-3">
              {!isSent ? (
                <>
                  {/* Cancel/Back button */}
              <button
                onClick={handleClose}
                    disabled={isSending}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium transition-colors duration-200 hover:bg-gray-200 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
              
                  {/* Send Treat button */}
              <PastelButton
                onClick={handleSendTreat}
                    disabled={!selectedTreat || isSending}
                className={`flex-1 text-sm ${
                      !selectedTreat || isSending
                    ? 'bg-gray-200 text-gray-600 cursor-not-allowed hover:from-gray-200 hover:to-gray-200'
                    : ''
                }`}
                    animate={selectedTreat && !isSending}
                color="rose"
              >
                    {isSending ? (
                  <div className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-pink-700 border-t-transparent rounded-full"
                    />
                    Sending...
                  </div>
                ) : (
                  `Send Treat ${selectedTreat ? '🎁' : ''}`
                )}
              </PastelButton>
                </>
              ) : (
                <>
                  {/* Back button after sent */}
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium transition-colors duration-200 hover:bg-gray-200 text-sm"
                  >
                    ← Back
                  </button>
                  
                  {/* Send Another Treat button */}
                  <PastelButton
                    onClick={handleSendAnother}
                    className="flex-1 text-sm"
                    animate={true}
                    color="lavender"
                  >
                    Send Another Treat 🎁
                  </PastelButton>
                </>
              )}
            </div>
          </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SendTreatsModal;